"use client";

import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { mintChildLoginToken } from "@/lib/api/members";

type Props = {
  childUid: string;
  username: string;
  slug: string;
};

/**
 * QR logowania dziecka — dwa warianty w jednym komponencie:
 *
 * Preferowany: jednorazowy custom token (mintChildLoginToken CF) —
 * dziecko skanuje zwykłym aparatem i jest zalogowane bez wpisywania
 * czegokolwiek. Token ważny maks. 1h i nie zawiera hasła — sfotografowany
 * kod po godzinie jest bezwartościowy.
 *
 * Fallback (mint w toku / błąd / offline): QR z prefillem slug+username —
 * dziecko dopisuje tylko hasło. Pokazywany od razu, podmieniany gdy token
 * dotrze, więc kod jest skanowalny bez czekania.
 */
export function ChildLoginQR({ childUid, username, slug }: Props) {
  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setToken(null);
    setFailed(false);
    mintChildLoginToken(childUid)
      .then((r) => {
        if (!cancelled) setToken(r.token);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [childUid]);

  const origin = window.location.origin;
  const url = token
    ? `${origin}/login?token=${encodeURIComponent(token)}`
    : `${origin}/login?slug=${encodeURIComponent(slug)}&username=${encodeURIComponent(username)}`;

  return (
    <div className="space-y-2">
      <div className="flex justify-center">
        <div className="rounded-[16px] border border-line bg-white p-3">
          <QRCodeSVG value={url} size={token ? 216 : 176} />
        </div>
      </div>
      <p className="text-center text-[11px] text-muted">
        {token
          ? "✅ Ten kod loguje od razu, bez hasła — ważny przez godzinę."
          : failed
            ? "Kod uzupełni rodzinę i nazwę — dziecko dopisze hasło."
            : "Przygotowuję kod szybkiego logowania…"}
      </p>
    </div>
  );
}
